import { periodLabel } from "./groupFormat";

/**
 * The participants' and trainers' portal — what the pages need to decide,
 * kept out of the components so it can be tested without rendering them.
 */

/**
 * The login token rides in the URL's fragment (`/portal/intrare#t=…`): the
 * browser never sends a fragment to a server, so the token reaches no access
 * log and no Referer header. Read once, then wiped from the address bar.
 */
export function tokenFromHash(hash) {
  const raw = String(hash || "").replace(/^#/, "");
  return new URLSearchParams(raw).get("t") || "";
}

/**
 * The groups, in the order and under the headings the home page shows.
 * Somebody can be both a participant and a trainer — the portal shows both
 * rather than make them pick, and only then says which is which.
 */
export function portalSections(me) {
  const asParticipant = me?.participant || [];
  const asTrainer = me?.trainer || [];
  const both = asParticipant.length > 0 && asTrainer.length > 0;
  const out = [];
  if (asParticipant.length) {
    out.push({
      key: "participant",
      title: both ? "Grupele mele, ca participant" : asParticipant.length === 1 ? "Grupa mea" : "Grupele mele",
      groups: asParticipant,
    });
  }
  if (asTrainer.length) {
    out.push({ key: "trainer", title: "Grupele în care predau", groups: asTrainer });
  }
  const asEvaluator = me?.evaluator || [];
  if (asEvaluator.length) {
    out.push({ key: "evaluator", title: "Grupele pe care le evaluez", groups: asEvaluator });
  }
  return out;
}

/** The line under a group's name: its dates, and who teaches it — except to
 *  the person who teaches it. */
export function groupMeta(group, role) {
  const parts = [];
  if (group?.start_date) parts.push(periodLabel(group.start_date, group.end_date));
  if (role !== "trainer" && group?.trainer_name) parts.push(`Formator: ${group.trainer_name}`);
  if (role === "evaluator" && group?.until) parts.push(`Evaluarea se încheie pe ${longDay(group.until)}`);
  return parts.join(" · ");
}

const longDay = (iso) =>
  new Intl.DateTimeFormat("ro-RO", { timeZone: "Europe/Bucharest", day: "numeric", month: "long" })
    .format(new Date(iso));

/**
 * What a participant who has withdrawn reads at the top: how long they still
 * have, and what for. Null for everybody else.
 */
export function readOnlyNote(me) {
  if (!me?.read_only) return null;
  const until = (me.participant || [])
    .map((p) => p.read_only_until)
    .filter(Boolean)
    .sort()[0];
  if (!until) return null;
  return `Înscrierea ta s-a încheiat. Până pe ${longDay(until)} poți intra aici să descarci ce ai încărcat; după aceea, accesul la portal se închide.`;
}

/** First entry: the note on how files are used opens by itself, once. After
 *  that it stays one click away, at the bottom of the page. */
export function noticeOpensByItself(me) {
  return Boolean(me) && !me.notice_seen_at;
}

export function greeting(me) {
  return me?.first_name ? `Bună, ${me.first_name}!` : "Bună!";
}

// The final feedback questionnaire (30.10.2026), for both regimes.
const FEEDBACK_LINE =
  "Chestionarul de feedback de la final e anonim: vedem doar că l-ai completat, nu ce ai răspuns. Dacă lași un testimonial și bifezi acordul de publicare, testimonialul se păstrează separat, cu numele tău.";

/** Draft wording — to be checked against the contract's information note. */
export const NOTICE_LINES = [
  "Ce încarci aici — teme, proiecte, portofoliul final — se păstrează la Fundația pentru Educație Digitală, pe servere din Uniunea Europeană.",
  "Le vede formatorul grupei tale și echipa FPED; portofoliul final îl vede și comisia de evaluare. Colegii de grupă nu le văd.",
  "Fac parte din dosarul cursului, pe care îl păstrăm pentru controale și reacreditare. Nu se șterg automat, iar o copie a lor ne poți cere oricând.",
  // The initial assessment (15.10.2026), validated with its texts.
  "Răspunsurile la chestionarul de evaluare inițială le vede formatorul grupei tale și echipa FPED. Ne ajută să adaptăm cursul și fac parte din dosarul lui.",
  FEEDBACK_LINE,
];

/** The same, for somebody whose cohorts are all Curs FPED (12.10.2026): no
 *  portfolio, no committee, no reaccreditation. Draft wording, like the above. */
export const NOTICE_LINES_FPED = [
  "Ce încarci aici — teme și proiecte — se păstrează la Fundația pentru Educație Digitală, pe servere din Uniunea Europeană.",
  "Le vede formatorul grupei tale și echipa FPED. Colegii de grupă nu le văd.",
  "Fac parte din dosarul cursului. Nu se șterg automat, iar o copie a lor ne poți cere oricând.",
  FEEDBACK_LINE,
];

export function noticeLines(me) {
  const cards = [...(me?.participant || []), ...(me?.trainer || []), ...(me?.evaluator || [])];
  return cards.length > 0 && cards.every((c) => c.regime === "fped") ? NOTICE_LINES_FPED : NOTICE_LINES;
}

export const SENT_NOTE =
  "Dacă adresa are acces la portal, îți trimitem un link în câteva minute. Uită-te și în Spam. Linkul ține 30 de minute și merge o singură dată.";
